/**
 * SampleDatasetsDialog
 *
 * The full catalog of curated sample datasets — the same ones offered during
 * onboarding, presented with the same picker UI (grouped accent tiles, pick
 * one, confirm). Picking one builds the CSV client-side and imports it
 * through the same inspect-file → import-file path onboarding uses, so
 * headline/tweet columns register as real EVENTS (not string signals), then
 * polls the execution until the import completes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Stack,
  Button, Chip, Alert, LinearProgress, ButtonBase, CircularProgress,
  useTheme, alpha, lighten,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScienceIcon from '@mui/icons-material/Science';

import { USE_CASES, USE_CASE_GROUPS, getUseCaseById } from '../../data/sampleData';
import {
  fetchRequest as loadSampleRequest,
  clear as clearLoadSample,
} from '../../redux/actions/datasources/loadSample';

// Same tile as the onboarding "What do you want to predict?" picker.
const UseCaseTile = ({ uc, accent, selected, onClick, dashed = false }) => {
  const theme = useTheme();
  const iconBg = selected ? accent : alpha(accent, theme.palette.mode === 'dark' ? 0.18 : 0.1);
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
        width: '100%', gap: 1, px: 1.25, py: 0.75,
        borderRadius: '10px', textAlign: 'left',
        border: `1.5px ${dashed && !selected ? 'dashed' : 'solid'} ${selected ? accent : theme.palette.divider}`,
        bgcolor: selected ? alpha(accent, theme.palette.mode === 'dark' ? 0.16 : 0.06) : 'transparent',
        transition: 'border-color 120ms ease, background-color 120ms ease, transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: selected ? accent : alpha(accent, 0.55),
          bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.12 : 0.045),
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 14px -4px ${alpha(accent, 0.35)}`,
        },
      }}
    >
      <Box sx={{
        width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: iconBg,
        color: selected ? '#fff' : (theme.palette.mode === 'dark' ? lighten(accent, 0.35) : accent),
        transition: 'background-color 120ms ease, color 120ms ease',
      }}>
        <uc.Icon sx={{ fontSize: 15 }} />
      </Box>
      <Typography sx={{
        fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, flex: 1,
        color: 'text.primary',
      }}>
        {uc.label}
      </Typography>
      <CheckCircleIcon sx={{
        fontSize: 16, flexShrink: 0, color: accent,
        opacity: selected ? 1 : 0, transform: selected ? 'scale(1)' : 'scale(0.5)',
        transition: 'opacity 140ms ease, transform 140ms ease',
      }} />
    </ButtonBase>
  );
};

export default function SampleDatasetsDialog({ open, onClose }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [useCase, setUseCase] = useState(null);
  // job: { useCaseId, status: 'uploading'|'processing'|'completed'|'failed',
  //        progress, name, rows, error } — the whole flow (stage -> import ->
  // poll) lives in the loadSample saga; the slice's response is the job.
  const job = useSelector((s) => s.datasource_load_sample?.response) || null;

  // Cancel an in-flight import + polling when the dialog unmounts.
  useEffect(() => () => { dispatch(clearLoadSample()); }, [dispatch]);

  const busy = job && (job.status === 'uploading' || job.status === 'processing');

  const ucById = useMemo(() => {
    const m = {};
    USE_CASES.forEach((u) => { m[u.id] = u; });
    return m;
  }, []);
  const otherUc = ucById.other;
  const otherAccent = theme.brand?.teal || theme.palette.primary.main;

  // Same path onboarding uses: stage via inspect-file, then import with
  // explicit field mappings so event columns register as events. The whole
  // flow (including execution polling) runs in the loadSample saga.
  const loadSample = useCallback((useCaseId) => {
    dispatch(loadSampleRequest({ useCaseId }));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    if (busy) return; // don't lose the import mid-flight
    onClose();
    dispatch(clearLoadSample());
    setUseCase(null);
  }, [busy, onClose, dispatch]);

  const jobUc = job ? getUseCaseById(job.useCaseId) : null;
  const selectedUc = useCase ? getUseCaseById(useCase) : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { maxHeight: '88vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Pick a sample dataset
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', mt: 0.5 }}>
            The same curated datasets from onboarding — two years of realistic
            daily data per scenario (staffing is hourly), imported as a new source.
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={busy}
          sx={{ position: 'absolute', right: 10, top: 10 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {job ? (
          <Box sx={{ py: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
              {job.status === 'completed'
                ? <CheckCircleIcon color="success" />
                : job.status === 'failed'
                  ? <Box sx={{ color: 'error.main', fontWeight: 700 }}>✕</Box>
                  : <ScienceIcon sx={{ color: 'primary.main' }} />}
              <Typography sx={{ fontWeight: 600, flex: 1 }} noWrap>
                {job.status === 'completed'
                  ? `${job.name} imported`
                  : job.status === 'failed'
                    ? `${job.name} failed to import`
                    : job.status === 'uploading'
                      ? `Uploading ${job.name}…`
                      : `Importing ${job.name}…`}
              </Typography>
              {jobUc && <Chip size="small" label={jobUc.label} sx={{ height: 20, fontSize: 11 }} />}
            </Stack>

            {job.status !== 'failed' && (
              <>
                <LinearProgress variant="determinate" value={job.progress || 0} sx={{ borderRadius: 1, height: 6 }} />
                {job.status !== 'completed' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    Importing signals and registering the headline columns as events
                    {job.rows ? ` — ${job.rows.toLocaleString()} rows so far` : ''}…
                  </Typography>
                )}
              </>
            )}

            {job.status === 'completed' && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
                <Typography variant="body2" color="success.main" sx={{ flex: 1 }}>
                  Imported {(job.rows || 0).toLocaleString()} rows.
                </Typography>
                <Button size="small" sx={{ textTransform: 'none' }}
                  onClick={() => { handleClose(); navigate('/datasources/uploads'); }}>
                  View imports
                </Button>
                <Button size="small" sx={{ textTransform: 'none' }}
                  onClick={() => { dispatch(clearLoadSample()); setUseCase(null); }}>
                  Load another
                </Button>
              </Stack>
            )}

            {job.status === 'failed' && (
              <Alert severity="error" sx={{ mt: 1 }}
                action={<Button size="small" color="inherit" sx={{ textTransform: 'none' }} onClick={() => dispatch(clearLoadSample())}>Back</Button>}>
                {job.error}
              </Alert>
            )}
          </Box>
        ) : (
          <Stack spacing={2.25}>
            {USE_CASE_GROUPS.map((group) => (
              <Box key={group.id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '3px', bgcolor: group.accent, flexShrink: 0 }} />
                  <Typography sx={{
                    fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'text.secondary',
                  }}>
                    {group.label}
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Stack>
                <Box sx={{
                  display: 'grid', gap: 0.75,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                }}>
                  {group.ids.map((id) => (
                    <UseCaseTile
                      key={id}
                      uc={ucById[id]}
                      accent={group.accent}
                      selected={useCase === id}
                      onClick={() => setUseCase(id)}
                    />
                  ))}
                </Box>
              </Box>
            ))}

            <UseCaseTile
              uc={otherUc}
              accent={otherAccent}
              selected={useCase === 'other'}
              onClick={() => setUseCase('other')}
              dashed
            />
          </Stack>
        )}
      </DialogContent>

      {!job && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            sx={{ minWidth: { xs: '100%', sm: 340 }, px: 4, textTransform: 'none' }}
            endIcon={busy ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />}
            disabled={!useCase || busy}
            onClick={() => loadSample(useCase)}
          >
            Load sample dataset
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, minHeight: 18 }}>
            {selectedUc
              ? selectedUc.sample.description
              : 'Pick a category above to continue.'}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}
